import React from 'react';
import { KostnadsKalkyleModalTitle } from './KostnadsKalkyleModalTitle';
import { KostnadsKalkyleModalCloseButton } from './KostnadsKalkyleModalCloseButton';
import { useKostnadsKalkyleContext } from '../context/KostnadsKalkyleProvider';
import type { TabProps, Theme } from '@material-ui/core';
import { Paper, Tab, Tabs, Tooltip } from '@material-ui/core';
import zIndex from '@material-ui/core/styles/zIndex';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { WithSubComponents } from '@forvalter/utils/TypeScriptUtils';
import { KostnadsKalkyleModalTrigger } from './KostnadsKalkyleModalTrigger';
import { KostnadsKalkyleForm } from '../Form/KostnadsKalkyleForm';
import { TabContext, TabPanel } from '@material-ui/lab';
import { StickyBox } from '@klp/design/StickyBox/StickyBox';
import { Block, KlpModal } from '@klp/design';
import { useTranslation } from 'react-i18next';
import { LonnsomhetGeneralData } from '../../Lonnsomhet/LonnsomhetGeneralData';
import { LonnsomhetTable } from '../../Lonnsomhet/LonnsomhetTable';
import { KostnadstyperTable } from '../../Kostnadstyper/KostnadstyperTable';
import type { KostnadsKalkyleTab } from '../context/disabledTabs';

type KostnadsKalkyleModalSubComponents = {
    Title: typeof KostnadsKalkyleModalTitle;
    CloseButton: typeof KostnadsKalkyleModalCloseButton;
    Trigger: typeof KostnadsKalkyleModalTrigger;
};

const styles = (theme: Theme) => ({
    modal: {
        maxWidth: '1800px',

        '& .Mui-disabled': {
            backgroundColor: theme.palette.grey[100],
        },
    },
    generalDataPaper: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.main,
    },
    contentBlock: {
        backgroundColor: theme.palette.background.paper,
    },
});

// Tabs kloner sine direkte barn med value/selected/onChange, så wrapperen må videresende
// Tab-props. Span-en trengs fordi en deaktivert Tab ikke sender mus-events til Tooltip.
// Inline z-index fordi index.scss legger alle tooltips (1198) under modalen.
const DisabledTab: React.FC<TabProps & { tooltip: string }> = ({
    tooltip,
    ...tabProps
}) => (
    <Tooltip
        title={tooltip}
        PopperProps={{ style: { zIndex: zIndex.modal + 1 } }}
    >
        <span>
            <Tab {...tabProps} disabled={true} />
        </span>
    </Tooltip>
);

export const KostnadsKalkyleModal: WithSubComponents<
    React.FC,
    KostnadsKalkyleModalSubComponents
> = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const {
        isModalOpen,
        toggleModal,
        selectedTab,
        setSelectedTab,
        prosjektNavn,
        disabledTabs,
    } = useKostnadsKalkyleContext();

    const tabs: Array<{ value: KostnadsKalkyleTab; label: string }> = [
        {
            value: 'kostnadskalkyle',
            label: t('prosjekt.kostnadskalkyle.title.tab'),
        },
        { value: 'lønnsomhet', label: t('prosjekt.lonnsomhet.title') },
        { value: 'kostnadstyper', label: t('prosjekt.kostnadstyper.title') },
    ];

    const handleModalClose = () => {
        toggleModal(false);
    };

    return (
        <KlpModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            translation="prosjekt.kostnadskalkyle.title.kalkyle"
            translationOptions={{
                prosjektNavn: prosjektNavn,
            }}
            className={classes.modal}
        >
            <Block className={classes.contentBlock} marginHorizontal="compact">
                <TabContext value={selectedTab}>
                    <StickyBox isInsideModal={true}>
                        <Block paddingTop="compact" paddingHorizontal="compact">
                            <Tabs
                                value={selectedTab}
                                onChange={(_event, newValue) =>
                                    setSelectedTab(newValue)
                                }
                            >
                                {tabs.map(({ value, label }) =>
                                    disabledTabs.has(value) ? (
                                        <DisabledTab
                                            key={value}
                                            value={value}
                                            label={label}
                                            tooltip={t(
                                                'prosjekt.kostnadskalkyle.disabledTab',
                                            )}
                                        />
                                    ) : (
                                        <Tab
                                            key={value}
                                            value={value}
                                            label={label}
                                        />
                                    ),
                                )}
                            </Tabs>
                        </Block>
                    </StickyBox>

                    <KostnadsKalkyleForm>
                        <KostnadsKalkyleForm.Autosave />

                        <TabPanel value="kostnadskalkyle">
                            <Block marginBottom="fat">
                                <Paper className={classes.generalDataPaper}>
                                    <KostnadsKalkyleForm.GeneralData />
                                </Paper>
                            </Block>
                            <Block marginBottom="bulky">
                                <Paper>
                                    <KostnadsKalkyleForm.Table />
                                </Paper>
                            </Block>
                            <Block marginBottom="bulky">
                                <KostnadsKalkyleForm.TilleggSammendrag />
                            </Block>
                            <KostnadsKalkyleForm.NokkeltallSammendrag />
                        </TabPanel>

                        {!disabledTabs.has('lønnsomhet') && (
                            <TabPanel value="lønnsomhet">
                                <Block marginBottom="fat">
                                    <Paper className={classes.generalDataPaper}>
                                        <LonnsomhetGeneralData />
                                    </Paper>
                                </Block>
                                <Paper>
                                    <LonnsomhetTable />
                                </Paper>
                            </TabPanel>
                        )}

                        <TabPanel value="kostnadstyper">
                            <KostnadstyperTable />
                        </TabPanel>
                    </KostnadsKalkyleForm>
                </TabContext>
            </Block>
        </KlpModal>
    );
};

KostnadsKalkyleModal.Title = KostnadsKalkyleModalTitle;
KostnadsKalkyleModal.CloseButton = KostnadsKalkyleModalCloseButton;
KostnadsKalkyleModal.Trigger = KostnadsKalkyleModalTrigger;
